/**
 * タスク・マップデータを **ブラウザが json.tarkov.dev から直接取得**し、
 * IndexedDB に7日キャッシュする。
 *
 * ゲーム内テキストは Battlestate Games の著作物で再配布できないので、ビルドにも
 * サーバにも同梱しない。→ doc/service/master.md#タスクデータの取得
 *
 * `scripts/fetch-tarkov.js`（開発用の取得スクリプト）と `server/tasks.js`（UI 向けの整形）が
 * やっていた2段を、ここで1段にまとめている。出来上がりの形は同じ。
 */
import { idbGet, idbPut } from './idb'
import { dictNames, loadTranslated, fetchJson, makeLookup } from './tarkovJson'
import type {
  GamePos,
  Lang,
  MapExtract,
  MapMeta,
  Task,
  TaskObjective,
  TaskPrepGroup,
  TaskPrepKind,
  TaskZone,
} from './types'

/** キャッシュ期限。切れていても**まず古いキャッシュで描き始め**、裏で取り直す */
const TTL_MS = 7 * 24 * 60 * 60 * 1000
/**
 * キャッシュは**言語ごとに別のキー**。切り替えても取り直しは初回だけで、戻せば効く。
 * `v1` は言語の区別が無いので読まずに捨てる（データは取り直せる → doc/i18n/master.md）。
 */
const coreKey = (lang: Lang) => `tarkov/core/v3/${lang}`
const itemsKey = (lang: Lang) => `tarkov/items/v2/${lang}`

/**
 * 同じ場所を別 id で持っているマップ。zone も extract も本体に寄せる。
 * ground-zero-21 はレベル20超え用、night-factory は Factory の夜。地形は同じ。
 */
const ALIASES: Record<string, string> = {
  'ground-zero-21': 'ground-zero',
  'ground-zero-tutorial': 'ground-zero',
  'night-factory': 'factory',
}

const PREP_ORDER: TaskPrepKind[] = ['key', 'plant', 'marker', 'weapon', 'build']

/**
 * 準備物の生の候補リスト。**名前を解決する前**の形。
 *
 * items（16.7MB）は準備物パネルを開くまで取りに行かないので、タスク本体と
 * 準備物の名前解決は分かれている。→ doc/task-prep/master.md#16mbをどう扱うか
 */
export type PrepRawGroup = {
  kind: TaskPrepKind
  ids: string[]
  /** `wearing` のように生データが名前を持っている場合だけ入る */
  names: (string | null)[]
  count?: number
}

export type CoreData = {
  /** `prep` は空。`applyPrep()` で後から埋める */
  tasks: Task[]
  prepRaw: Record<string, PrepRawGroup[]>
  mapMeta: Record<string, MapMeta>
  traders: string[]
  maps: string[]
  fetchedAt: string
}

export type ItemNames = Record<string, { name: string; short: string | null }>

/* ------------------------------------------------------------------ *
 * 正規化
 * ------------------------------------------------------------------ */

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n)

function pos(raw: any): GamePos | null {
  if (!raw || !isNum(raw.x) || !isNum(raw.y) || !isNum(raw.z)) return null
  return { x: raw.x, y: raw.y, z: raw.z }
}

const outline = (raw: any): GamePos[] =>
  Array.isArray(raw) ? (raw.map(pos).filter(Boolean) as GamePos[]) : []

/** 同じ場所を指す zone をまとめるためのキー。cm 単位まで合えば同じとみなす */
const spotKey = (map: string, p: any) =>
  `${map}@${(p?.x ?? 0).toFixed(2)},${(p?.y ?? 0).toFixed(2)},${(p?.z ?? 0).toFixed(2)}`

function buildObjective(raw: any, mapName: (id: string) => string | null): TaskObjective {
  const seen = new Set<string>()
  const zones: TaskZone[] = []
  for (const z of raw?.zones ?? []) {
    const map = mapName(z?.map)
    if (!map) continue
    const position = pos(z.position)
    if (!position) continue
    const key = spotKey(map, position)
    if (seen.has(key)) continue
    seen.add(key)
    zones.push({
      map,
      position,
      outline: outline(z.outline),
      top: isNum(z.top) ? z.top : null,
      bottom: isNum(z.bottom) ? z.bottom : null,
    })
  }
  return {
    id: typeof raw?.id === 'string' ? raw.id : '',
    type: typeof raw?.type === 'string' ? raw.type : '',
    description: typeof raw?.description === 'string' ? raw.description : '',
    count: isNum(raw?.count) && raw.count > 1 ? raw.count : null,
    optional: raw?.optional === true,
    maps: [...new Set((raw?.maps ?? []).map((m: string) => mapName(m)).filter(Boolean))] as string[],
    zones,
  }
}

function buildTask(raw: any, mapName: (id: string) => string | null, traderName: (id: string) => string): Task {
  const objectives = (raw.objectives ?? []).map((o: any) => buildObjective(o, mapName))

  // 「このタスクがどのマップに関係するか」。検索の絞り込みと、
  // マップタブの一覧に出すかの判定に使う。空なら場所の概念が無いタスク
  const maps = new Set<string>()
  const main = mapName(raw.map)
  if (main) maps.add(main)
  for (const o of objectives) {
    for (const m of o.maps) maps.add(m)
    for (const z of o.zones) maps.add(z.map)
  }

  return {
    id: raw.id,
    name: raw.name,
    trader: traderName(raw.trader),
    map: main,
    maps: [...maps],
    kappaRequired: raw.kappaRequired === true,
    minPlayerLevel: isNum(raw.minPlayerLevel) ? raw.minPlayerLevel : 0,
    wikiLink: typeof raw.wikiLink === 'string' ? raw.wikiLink : '',
    taskRequirements: (raw.taskRequirements ?? []).map((r: any) => r?.task).filter((t: any) => typeof t === 'string'),
    objectives,
    // 名前が要るので、items を取るまで空。→ applyPrep()
    prep: [],
    hasZones: objectives.some((o: TaskObjective) => o.zones.length > 0),
  }
}

/**
 * マップの付随データ。**脱出地点だけを配る。**
 * locks / hazards / switches も上流にはあるが、Lighthouse の hazard だけで 344 件あり、
 * 今は描く先が無いので載せない。
 */
function buildMapMeta(maps: Record<string, any>, mapName: (id: string) => string | null) {
  const out: Record<string, MapMeta> = {}
  // エイリアス（night-factory など）は本体に寄せる。先に本体を作ってから足す
  const entries = Object.values(maps).sort(
    (a: any, b: any) => Number(a.normalizedName in ALIASES) - Number(b.normalizedName in ALIASES),
  )
  for (const m of entries as any[]) {
    const key = mapName(m.id)
    if (!key) continue
    const entry = (out[key] ??= { id: m.id, name: m.name, aliases: [], extracts: [] })
    if (m.normalizedName !== key) entry.aliases.push(m.normalizedName)

    const seen = new Set(entry.extracts.map((e) => spotKey(key, e.position)))
    for (const e of m.extracts ?? []) {
      const p = pos(e?.position)
      if (!p || typeof e.name !== 'string') continue
      const k = spotKey(key, p)
      if (seen.has(k)) continue
      seen.add(k)
      const extract: MapExtract = {
        id: typeof e.id === 'string' ? e.id : e.name,
        name: e.name,
        faction: typeof e.faction === 'string' ? e.faction : '',
        position: p,
        outline: outline(e.outline),
        top: isNum(e.top) ? e.top : null,
        bottom: isNum(e.bottom) ? e.bottom : null,
      }
      entry.extracts.push(extract)
    }
  }
  return out
}

/* ------------------------------------------------------------------ *
 * 準備物（scripts/lib/prep.js の移植）
 *
 * あちらは「id を集める」と「名前を貰って組み立てる」の2段だが、こちらは
 * items の取得が遅延なので「生の候補リストを作る」と「名前を当てる」に分けてある。
 * ------------------------------------------------------------------ */

const isId = (v: unknown): v is string => typeof v === 'string' && v.length > 0
const idOf = (v: any): string | null => (isId(v) ? v : isId(v?.id) ? v.id : null)
const nameOf = (v: any): string | null => (typeof v?.name === 'string' && v.name ? v.name : null)

/**
 * 1タスクぶんの候補リストを列挙する。
 * 1つの候補リスト＝「このうちどれか1つでよい」。複数のリストは全部要る（AND）。
 *
 * 納品（giveItem）と売却（sellItem）は載せない。前者はレイド内で拾う話で、
 * 後者は1タスクで数千アイテムが候補になり一覧が壊れるため。
 */
function* groupsOf(task: any): Generator<{ kind: TaskPrepKind; raw: any[]; count?: number }> {
  for (const needed of task.neededKeys ?? []) {
    // neededKeys はマップごとの「要る鍵」。各鍵が独立に要るので1件ずつのリストにする
    for (const key of needed?.keys ?? []) yield { kind: 'key', raw: [key] }
  }

  for (const obj of task.objectives ?? []) {
    for (const alts of obj.requiredKeys ?? []) {
      if (Array.isArray(alts) && alts.length) yield { kind: 'key', raw: alts }
    }
    if (obj.markerItem) yield { kind: 'marker', raw: [obj.markerItem] }

    if (obj.type === 'plantItem' || obj.type === 'plantQuestItem') {
      if (obj.items?.length) yield { kind: 'plant', raw: obj.items, count: obj.count }
    }

    if (obj.type === 'buildWeapon') {
      if (obj.item) yield { kind: 'build', raw: [obj.item] }
      // containsAll は「全部組み込む」。1件ずつ独立したリストにする
      for (const mod of obj.containsAll ?? []) yield { kind: 'build', raw: [mod] }
    }

    if (obj.usingWeapon?.length) yield { kind: 'weapon', raw: obj.usingWeapon }
    for (const slot of obj.usingWeaponMods ?? []) {
      if (Array.isArray(slot) && slot.length) yield { kind: 'weapon', raw: slot }
    }
    for (const worn of obj.wearing ?? []) {
      const list = Array.isArray(worn) ? worn : [worn]
      if (list.length) yield { kind: 'weapon', raw: list }
    }
  }
}

function buildPrepRaw(task: any): PrepRawGroup[] {
  const out: PrepRawGroup[] = []
  for (const group of groupsOf(task)) {
    const ids: string[] = []
    const names: (string | null)[] = []
    for (const raw of group.raw) {
      const id = idOf(raw)
      if (!id || ids.includes(id)) continue
      ids.push(id)
      names.push(nameOf(raw))
    }
    if (ids.length) out.push({ kind: group.kind, ids, names, count: group.count })
  }
  return out
}

/** 生の候補リストに名前を当てて、画面に出せる形にする。名前が引けない候補は落とす */
export function buildPrep(
  groups: PrepRawGroup[],
  itemName: (id: string) => string | null,
  lang: Lang,
): TaskPrepGroup[] {
  const out: TaskPrepGroup[] = []
  const seen = new Set<string>()

  for (const group of groups) {
    const items: { id: string; name: string; count?: number }[] = []
    group.ids.forEach((id, i) => {
      const name = group.names[i] ?? itemName(id)
      if (!name) return
      items.push(group.count && group.count > 1 ? { id, name, count: group.count } : { id, name })
    })
    if (!items.length) continue

    // 同じ中身のリストが複数の目標から出てくることがある（同じ鍵を2つの目標が要る等）
    const key = `${group.kind}:${items
      .map((it) => it.id)
      .sort()
      .join(',')}:${group.count ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)

    items.sort((a, b) => a.name.localeCompare(b.name, lang))
    out.push({ kind: group.kind, items })
  }

  out.sort((a, b) => PREP_ORDER.indexOf(a.kind) - PREP_ORDER.indexOf(b.kind))
  return out
}

/**
 * 目標1件の表示文。**個数を後ろに足す**（`生地 を渡す ×5`）。
 *
 * 上流の日本語訳は品名を落とすことがあり、`Textile - Part 1` は
 * 「アラミド繊維の生地」と「リップストップの生地」がどちらも `生地 を渡す` になる。
 * 個数まで出せば別物と分かるうえ、そもそも「何個要るのか」は元の文に入っていない。
 */
export const objectiveText = (o: TaskObjective) =>
  o.count && o.count > 1 ? `${o.description} ×${o.count}` : o.description

/** タスク一覧に準備物を当てた新しい配列を返す（元は書き換えない） */
export function applyPrep(
  tasks: Task[],
  prepRaw: Record<string, PrepRawGroup[]>,
  items: ItemNames,
  lang: Lang,
): Task[] {
  const itemName = (id: string) => items[id]?.name ?? null
  return tasks.map((t) => {
    const groups = prepRaw[t.id]
    if (!groups?.length) return t
    return { ...t, prep: buildPrep(groups, itemName, lang) }
  })
}

/* ------------------------------------------------------------------ *
 * 取得とキャッシュ
 * ------------------------------------------------------------------ */

type Cached<T> = { at: number; data: T }

const fresh = (c: Cached<unknown> | undefined) => Boolean(c && Date.now() - c.at < TTL_MS)

/** tasks / maps / traders を取って UI の形にする */
async function fetchCore(lang: Lang, signal?: AbortSignal): Promise<CoreData> {
  const [tasksData, mapsData, tradersData] = await Promise.all([
    loadTranslated('regular/tasks', lang, signal),
    loadTranslated('regular/maps', lang, signal),
    loadTranslated('regular/traders', lang, signal),
  ])

  // map id → normalizedName。エイリアスは本体に寄せる
  const nameOfMap = new Map<string, string>()
  for (const m of Object.values<any>(mapsData.maps)) {
    nameOfMap.set(m.id, ALIASES[m.normalizedName] ?? m.normalizedName)
  }
  const mapName = (id: string) => (id ? nameOfMap.get(id) ?? null : null)

  const traderOf = new Map<string, string>(Object.entries<any>(tradersData).map(([id, t]) => [id, t.name]))
  const traderName = (id: string) => traderOf.get(id) ?? ''

  const tasks: Task[] = []
  const prepRaw: Record<string, PrepRawGroup[]> = {}
  for (const raw of Object.values<any>(tasksData.tasks)) {
    if (!raw || typeof raw.id !== 'string' || typeof raw.name !== 'string') continue
    tasks.push(buildTask(raw, mapName, traderName))
    const groups = buildPrepRaw(raw)
    if (groups.length) prepRaw[raw.id] = groups
  }

  return {
    tasks,
    prepRaw,
    mapMeta: buildMapMeta(mapsData.maps, mapName),
    traders: [...new Set(tasks.map((t) => t.trader).filter(Boolean))].sort(),
    // 検索の「マップ」欄に出す候補。データに実際に出てくるものだけ
    maps: [...new Set(tasks.flatMap((t) => t.maps))].sort(),
    fetchedAt: new Date().toISOString(),
  }
}

/**
 * タスク本体を読む。
 *
 * キャッシュがあれば**まずそれで描き始め**、期限切れなら裏で取り直して `onUpdate` を呼ぶ。
 * 上流が落ちていてもキャッシュがあれば動く（→ doc/service/master.md#タスクデータの取得）。
 */
export async function loadCore(lang: Lang, onUpdate?: (data: CoreData) => void): Promise<CoreData> {
  const key = coreKey(lang)
  const cached = await idbGet<Cached<CoreData>>(key)
  if (cached && fresh(cached)) return cached.data

  if (cached) {
    // 期限切れ。古いままでも描けるので、取り直しは裏でやる
    void fetchCore(lang)
      .then(async (data) => {
        await idbPut(key, { at: Date.now(), data })
        onUpdate?.(data)
      })
      .catch(() => {
        /* 取り直せなくても古いキャッシュで動き続ける */
      })
    return cached.data
  }

  const data = await fetchCore(lang)
  await idbPut(key, { at: Date.now(), data })
  return data
}

/**
 * 準備物のアイテム名。**16.7MB あるので、準備物を初めて開いたときだけ取りに行く。**
 * 取ったその場でタスクが参照する id（500件ほど）に絞り、絞った表だけを残す。
 */
export async function loadItemNames(ids: Set<string>, lang: Lang): Promise<ItemNames> {
  const key = itemsKey(lang)
  const cached = await idbGet<Cached<ItemNames>>(key)
  // 参照される id が全部そろっていれば使う（タスク側が更新されると増えることがある）
  if (cached && fresh(cached) && [...ids].every((id) => id in cached.data)) return cached.data

  const [base, tasksBase, ...dicts] = await Promise.all([
    fetchJson('regular/items'),
    // クエストアイテムは items に無く、タスク側に同梱されている（翻訳も当たっている）
    fetchJson('regular/tasks'),
    ...dictNames('regular/items', lang).map((d) => fetchJson(d)),
  ])
  const lookup = makeLookup(...dicts)
  const questItems: Record<string, any> = tasksBase.data.questItems ?? {}

  const items: ItemNames = {}
  for (const id of ids) {
    const it = base.data.items?.[id]
    if (it) {
      items[id] = { name: lookup(it.name) ?? it.normalizedName ?? id, short: lookup(it.shortName) ?? null }
    } else if (questItems[id]) {
      items[id] = { name: questItems[id].name, short: questItems[id].shortName ?? null }
    }
  }
  await idbPut(key, { at: Date.now(), data: items })
  return items
}

/** 全タスクが参照するアイテムIDの集合 */
export function collectPrepIds(prepRaw: Record<string, PrepRawGroup[]>): Set<string> {
  const ids = new Set<string>()
  for (const groups of Object.values(prepRaw)) for (const g of groups) for (const id of g.ids) ids.add(id)
  return ids
}
